import React, {useEffect, useState} from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from "react-native";
import {useDispatch, useSelector} from 'react-redux';

import {formatSharpHour, getDaySchedule} from "../services/helpers/dateTimeHelpers";
import moment from "moment";
import {
    getBookingList,
    getBookModalVisibility,
    getConfig,
    getConnectionStatus,
    getCurrentBooking,
    getEndModalVisibility,
    getExtendModalVisibility,
    getLoading,
    getNextBooking,
    getRoomStatus,
    getSpaceQrModalVisibility,
    setActionAuthentication,
    setBookingListExtended,
    setBookModalVisibility,
    setEndModalVisibility,
    setError,
    setExtendModalVisibility,
    setRoomStatus,
    setSpaceQrModalVisibility,
    setSuccess
} from "../reducers/configSlice";
import {CHECKIN_ACTION, EARLY_CHECKIN_ACTION, ROOM_STATUS_BUSY, ROOM_STATUS_WAITING} from "../constants/default";
import {apiAuthorizationToken, apiCheckinBooking, apiSaveBooking} from "../api/actions";
import moment_tz from "moment-timezone";
import {addActionAuthenticationListener} from "../services/actionAuthentication/actionAuthenticationService";
import {Icon} from "react-native-elements";
import {FONT_FAMILY} from "../constants/styleConstants";
import {actuatedNormalize, proportionalSize} from "../services/helpers/stylingHelpers";

const ButtonsContainer = () => {
    const dispatch = useDispatch()

    const connectionStatus = useSelector(getConnectionStatus)
    const config = useSelector(getConfig)
    const currentBooking = useSelector(getCurrentBooking)
    const nextBooking = useSelector(getNextBooking)
    const roomStatus = useSelector(getRoomStatus)
    const bookModalVisible = useSelector(getBookModalVisibility)
    const extendModalVisible = useSelector(getExtendModalVisibility)
    const endModalVisible = useSelector(getEndModalVisibility)
    const spaceQrModalVisible = useSelector(getSpaceQrModalVisibility)
    const loading = useSelector(getLoading)
    const bookingList = useSelector(getBookingList)

    const [hideExtend, setHideExtend] = useState(false)

    const confirmBooking = async () => {
        if (config.require_login_actions) {
            let action = CHECKIN_ACTION
            let data = {
                booking_id: currentBooking.booking_id
            }

            if (currentBooking.early && config.granularity <= 15) {
                action = EARLY_CHECKIN_ACTION

                const now = moment().tz(config.timezone)
                const minutesToSubtract = now.minute() % config.granularity
                const newStart = now.subtract(minutesToSubtract, "minutes").format('YYYY-MM-DD HH:mm')

                data.dates = [{
                    start: newStart,
                    end: currentBooking.end
                }]

                if (currentBooking.other_dates && currentBooking.other_dates.length) {
                    currentBooking.other_dates.forEach( (date, index) => {
                        data.dates.push({
                            start: date.start,
                            end: date.end
                        })
                    })
                }
            }


            const result = await apiAuthorizationToken(action, data)

            if (result.status === 1 && result.code && result.data) {
                const authAction = {
                    action: action,
                    successMessage: translate("Booking successfully checked-in"),
                    link: result.data,
                    code: result.code
                }

                await addActionAuthenticationListener(authAction)
                dispatch(setActionAuthentication(authAction))
            }
        } else {
            const { status, error } = await apiCheckinBooking(currentBooking)

            if (error) {
                dispatch(setError(error))
            } else {
                dispatch(setRoomStatus(ROOM_STATUS_BUSY))
                dispatch(setSuccess(translate('Booking successfully checked-in')))
            }
        }
    }

    const toggleEndModal = () => {
        dispatch(setEndModalVisibility(!endModalVisible))
        if (!endModalVisible) {
            dispatch(setBookingListExtended(false))
        }
    }

    const toggleExtendModal = () => {
        dispatch(setExtendModalVisibility(!extendModalVisible))
        if (!extendModalVisible) {
            dispatch(setBookingListExtended(false))
        }
    }

    const toggleBookModal = async () => {
        if (config.skip_booking_form && !currentBooking && config.intervals.custom) {
            const now = moment_tz.tz(moment().tz(config.timezone).format('YYYY-MM-DD HH:mm'), config.timezone).utc().tz(config.timezone)
            const minutesToSubtract = now.minute() % 5

            const start = moment_tz.tz(moment().tz(config.timezone).format('YYYY-MM-DD HH:mm'), config.timezone).utc().tz(config.timezone).subtract(minutesToSubtract, 'minutes')
            const end = moment_tz.tz(moment().tz(config.timezone).format('YYYY-MM-DD HH:mm'), config.timezone).utc().tz(config.timezone).subtract(minutesToSubtract, 'minutes').add(config.default_booking_duration, 'minutes')

            if (bookingList) {
                const overlappingBooking = bookingList.find( (booking, index) => {
                    const startMoment = moment_tz.tz(booking.start, config.timezone).utc().tz(config.timezone)
                    const endMoment = moment_tz.tz(booking.end, config.timezone).utc().tz(config.timezone)

                    if (startMoment.isBefore(end) && start.isBefore(endMoment)) {
                        return booking
                    }
                })

                if (overlappingBooking) {
                    dispatch(setBookingListExtended(false))
                    dispatch(setBookModalVisibility(!bookModalVisible))
                } else {
                    const data = {
                        space_id: config.space_id,
                        name: translate("Booking"),
                    }

                    data["dates[0][start]"] = start.format('YYYY-MM-DD HH:mm')
                    data["dates[0][end]"] = end.format('YYYY-MM-DD HH:mm')

                    const {status, error} = await apiSaveBooking(data)

                    if (!error) {
                        dispatch(setSuccess(translate('Booking successfully created')))
                    } else {
                        dispatch(setError(error))
                    }
                }
            }
        } else {
            dispatch(setBookingListExtended(false))
            dispatch(setBookModalVisibility(!bookModalVisible))
        }
    }

    const toggleSpaceQRCodeModal = () => {
        dispatch(setSpaceQrModalVisibility(!spaceQrModalVisible))
        if (!spaceQrModalVisible) {
            dispatch(setBookingListExtended(false))
        }
    }

    useEffect(() => {
        if (currentBooking) {
            const currentBookingEnd = moment_tz.tz(currentBooking.end, config.timezone).utc().tz(config.timezone)

            if (nextBooking) {
                const nextBookingStart = moment_tz.tz(nextBooking.start, config.timezone).utc().tz(config.timezone)

                setHideExtend(parseInt(nextBookingStart.diff(currentBookingEnd, 'minutes')) < 15);
            } else {
                const scheduleEndMoment = moment_tz.tz(moment().format('YYYY-MM-DD') + " " + formatSharpHour(getDaySchedule().end), config.timezone).utc().tz(config.timezone)

                setHideExtend(parseInt(scheduleEndMoment.diff(currentBookingEnd, 'minutes')) < 15);
            }
        }
    }, [currentBooking, nextBooking])

    return (
        <View style={styles.buttonsContainer}>
            {roomStatus === ROOM_STATUS_WAITING ?
                <ActionButton onPress={confirmBooking} style={[styles.confirmButton, {marginRight: '2%'}]}
                              buttonText={translate("Confirm")} key="confirm" disabled={!connectionStatus} reversed={true}/> : null
            }
            {roomStatus === ROOM_STATUS_BUSY && !hideExtend && !config.hide_controls ?
                <ActionButton onPress={toggleExtendModal} style={styles.extendButton}
                              buttonText={translate("Extend")} key="extend" disabled={!connectionStatus}
                /> : null
            }
            {roomStatus === ROOM_STATUS_BUSY && !config.hide_controls ?
                <ActionButton onPress={toggleEndModal} style={styles.endButton}
                              buttonText={translate("End")} key='end' disabled={!connectionStatus}
                /> : null
            }
            {config.allow_booking ?
                <ActionButton onPress={toggleBookModal} style={styles.bookButton}
                              buttonText={translate("Book now")} key='book' disabled={!connectionStatus}
                /> : null
            }
            {config.space_qr_code ?
                <ActionButton onPress={toggleSpaceQRCodeModal} style={styles.spaceQrButton}
                              buttonText={translate("Quick book")} icon="qrcode" key='qr' disabled={!connectionStatus}
                /> : null
            }
        </View>
    )
}

const ActionButton = ({disabled, onPress, style, reversed, buttonText, icon}) => {
    return (
        <TouchableOpacity disabled={disabled} onPress={onPress} style={[style, disabled && styles.disabled]}>
            <View style={{flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
                {icon ? <Icon size={proportionalSize(28)} name={icon} type="font-awesome" containerStyle={styles.iconStyle} color="#E3E3E3" /> : null}

                <Text style={[styles.buttonText, reversed && { color: '#000000'}]}>
                    {buttonText ? buttonText.toUpperCase() : null}
                </Text>
            </View>
        </TouchableOpacity>
    );
};

export default ButtonsContainer

const styles = StyleSheet.create({
    buttonsContainer: {
        flexDirection: 'row',
        height: actuatedNormalize(40),
        marginTop:'5%',
    },
    confirmButton: {
        borderWidth: 1,
        borderColor:'#FFFFFF',
        borderTopLeftRadius: 3,
        borderBottomLeftRadius: 3,
        paddingLeft: '3%',
        paddingRight: '3%',
        backgroundColor: 'rgba(255, 255, 255, 1)',
        justifyContent: 'center',
        height: actuatedNormalize(40)
    },
    extendButton: {
        borderWidth: 1,
        borderColor: '#FFFFFF',
        paddingLeft: '3%',
        paddingRight: '3%',
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        justifyContent: 'center',
        height: actuatedNormalize(40)
    },
    endButton: {
        borderWidth: 1,
        borderColor:'#FFFFFF',
        borderTopRightRadius: 3,
        borderBottomRightRadius: 3,
        marginRight: '2%',
        paddingLeft: '3%',
        paddingRight: '3%',
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        justifyContent: 'center',
        height: actuatedNormalize(40)
    },
    bookButton: {
        borderWidth: 2,
        borderColor:'#89999F',
        borderRadius: 3,
        paddingLeft: '3%',
        paddingRight: '3%',
        backgroundColor: 'rgba(200, 200, 200, 0.15)',
        justifyContent: 'space-around',
        alignItems: 'center',
        flexDirection:'row',
        height: actuatedNormalize(40)
    },
    spaceQrButton: {
        borderWidth: 2,
        borderColor:'#89999F',
        borderRadius: 3,
        paddingLeft: '3%',
        paddingRight: '3%',
        backgroundColor: 'rgba(200, 200, 200, 0.15)',
        justifyContent: 'space-around',
        alignItems: 'center',
        flexDirection:'row',
        height: actuatedNormalize(40),
        marginLeft: '2%'
    },
    buttonText: {
        color: '#FFFFFF',
        fontFamily:FONT_FAMILY,
        fontSize: actuatedNormalize(12)
    },
    iconStyle: {
        marginRight: actuatedNormalize(10),
        marginTop: actuatedNormalize(2)
    },
    disabled: {
        opacity: 0.5
    }
});
